import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';

import JsonSchema, { JsonSchemaComplexObject } from '@evidentid/json-schema/interfaces/JsonSchema';
import { TprmSchema } from '@evidentid/tprm-portal-lib/models/common/TprmSchema.model';
import { TprmSchemaDisplayFormat } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';

import { convertCustomPropertySchemaToEnumSchema } from '@/modules/decisioning-criteria/utils/criterionSchemaConversion';
import { getAllSubstitutableSchemas } from '@/modules/decisioning-criteria/utils/getAllSubstitutableSchemas';

/**
 * Fills in pickers that state their own filter, wherever they sit in the schema.
 *
 * `getAllSubstitutableSchemas` below recognises exactly one shape: a `*BasedOnInsuredField` boolean whose
 * `then` holds the picker and whose `else` holds the static alternative that the picker is filtered by. A
 * selector with more than two branches has no such boolean and no single opposite branch to infer a filter
 * from, so it declares `insuredFieldFilterSchema` on each picker instead and is invisible to that scan --
 * leaving the picker as a bare string field with no options.
 *
 * The two passes never touch the same node: a declared filter is precisely what the legacy shape does not
 * have, and vice versa.
 */
function applyDeclaredPickerFilters(schema: JsonSchema, customProperties: CustomProperty[]): JsonSchema {
    if (!schema || typeof schema !== 'object') {
        return schema;
    }

    const declaredFilterBy = (schema as any).insuredFieldFilterSchema as JsonSchema | undefined;
    if (declaredFilterBy && (schema as TprmSchema).displayFormat === TprmSchemaDisplayFormat.insuredField) {
        // `insuredFieldFilterSchema` is ours, not part of JSON Schema, so it is dropped here rather than left
        // to reach the form -- including when the filter matches nothing and the picker is returned as-is.
        return convertCustomPropertySchemaToEnumSchema(
            omit(schema, [ 'insuredFieldFilterSchema' ]) as JsonSchema,
            customProperties,
            declaredFilterBy,
        );
    }

    const result: any = { ...schema };
    if (result.properties) {
        result.properties = Object.keys(result.properties).reduce((acc, key) => ({
            ...acc,
            [key]: applyDeclaredPickerFilters(result.properties[key], customProperties),
        }), {});
    }
    if (result.items) {
        result.items = applyDeclaredPickerFilters(result.items, customProperties);
    }
    // Recurse into the branches themselves, not just their `properties`: a three-way selector nests a whole
    // conditional inside another's `else`, with no property in between.
    ([ 'if', 'then', 'else' ] as const).forEach((branch) => {
        if (result[branch]) {
            result[branch] = applyDeclaredPickerFilters(result[branch], customProperties);
        }
    });
    return result as JsonSchema;
}

export function standardizeCustomPropertySchema(
    schema: JsonSchema | JsonSchemaComplexObject,
    customProperties?: CustomProperty[],
): JsonSchema {
    const resultSchema = cloneDeep(schema);
    if (!customProperties) {
        return resultSchema;
    }
    const substitutableSchemas = getAllSubstitutableSchemas(resultSchema);
    substitutableSchemas.forEach(({ schema: substitutableSchema }) => {
        const valueSchema = substitutableSchema.else.properties!.value;
        const customPropertySchema = substitutableSchema.then.properties!.insuredField;
        substitutableSchema.then.properties!.insuredField
            = convertCustomPropertySchemaToEnumSchema(customPropertySchema, customProperties, valueSchema);
    });
    return applyDeclaredPickerFilters(resultSchema, customProperties);
}
